import { secondsToTicks } from "../constants";
import type { Vec2 } from "../types";

/**
 * One six-minute circuit. On the 1580x960 board the 4,856-unit perimeter moves
 * at 13.5 world units per second: visible at opening zoom, but slower than a
 * convoy and quiet enough to remain background motion.
 */
export const BROKER_ORBIT_PERIOD_TICKS = secondsToTicks(360);

const SIDE_INSET = 18;
const NORTHING = 76;

export interface BrokerOrbitBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/** A rectangular sea lane just beyond all four edges of the continent. */
export function brokerOrbitBounds(bounds: { width: number; height: number }): BrokerOrbitBounds {
  return {
    left: SIDE_INSET,
    right: bounds.width - SIDE_INSET,
    top: NORTHING,
    bottom: bounds.height,
  };
}

/**
 * Fleet position at a simulation tick, starting at north-centre and sailing
 * clockwise. It is derived from the tick, never from wall-clock time.
 */
export function brokerHubAt(bounds: { width: number; height: number }, tick: number): Vec2 {
  const route = brokerOrbitBounds(bounds);
  const width = Math.max(1, route.right - route.left);
  const height = Math.max(1, route.bottom - route.top);
  const perimeter = 2 * (width + height);
  const wrappedTick = ((tick % BROKER_ORBIT_PERIOD_TICKS) + BROKER_ORBIT_PERIOD_TICKS) %
    BROKER_ORBIT_PERIOD_TICKS;
  let travelled = (wrappedTick / BROKER_ORBIT_PERIOD_TICKS) * perimeter;
  const middleX = (route.left + route.right) / 2;

  const northToEast = width / 2;
  if (travelled <= northToEast) return { x: middleX + travelled, y: route.top };
  travelled -= northToEast;

  if (travelled <= height) return { x: route.right, y: route.top + travelled };
  travelled -= height;

  if (travelled <= width) return { x: route.right - travelled, y: route.bottom };
  travelled -= width;

  if (travelled <= height) return { x: route.left, y: route.bottom - travelled };
  travelled -= height;

  return { x: route.left + travelled, y: route.top };
}
